import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';

import {
  clearAllPersistedCacheByPrefix,
  getPersistedCache,
  setPersistedCache,
} from './persistedCache';

const CACHE_VERSION = 1;
const STORAGE_KEY_PREFIX = 'ahoy-cache-property-schemas-';
const COMPONENT = 'persisted-property-schemas';

function buildKey(orgId: number | string, entityType: string): string {
  return `${STORAGE_KEY_PREFIX}${orgId}-${entityType}`;
}

function isPropertyTypeSchemaShape(item: unknown): boolean {
  if (typeof item !== 'object' || item === null) {
    return false;
  }
  const p = item as Record<string, unknown>;
  const validReadAccess =
    p.value_read_access === 'all' || p.value_read_access === 'internal';
  const validWriteAccess =
    p.value_write_access === 'all' ||
    p.value_write_access === 'internal' ||
    p.value_write_access === 'none';
  const validUpdateAccess =
    p.value_update_access === 'all' || p.value_update_access === 'none';
  return (
    typeof p.property_type_name === 'string' &&
    typeof p.entity_type_name === 'string' &&
    typeof p.label === 'string' &&
    typeof p.field_type_name === 'string' &&
    typeof p.description === 'string' &&
    typeof p.is_required === 'boolean' &&
    typeof p.is_unique_value === 'boolean' &&
    (p.is_multi === undefined || typeof p.is_multi === 'boolean') &&
    validReadAccess &&
    validWriteAccess &&
    validUpdateAccess
  );
}

function isPropertySchemasShape(raw: unknown): raw is PropertyTypeSchema[] {
  return Array.isArray(raw) && raw.every(isPropertyTypeSchemaShape);
}

export function getPersistedPropertySchemas(
  orgId: number | string | undefined,
  entityType: string | undefined
): PropertyTypeSchema[] | undefined {
  if (orgId === undefined || !entityType) {
    return undefined;
  }
  return getPersistedCache<PropertyTypeSchema[]>(
    buildKey(orgId, entityType),
    CACHE_VERSION,
    isPropertySchemasShape,
    COMPONENT
  );
}

export function setPersistedPropertySchemas(
  orgId: number | string,
  entityType: string,
  schemas: PropertyTypeSchema[]
): void {
  setPersistedCache(
    buildKey(orgId, entityType),
    schemas,
    CACHE_VERSION,
    COMPONENT
  );
}

export function clearAllPersistedPropertySchemas(): void {
  clearAllPersistedCacheByPrefix(STORAGE_KEY_PREFIX, COMPONENT);
}
